<template>
    <vx-modal :visible="visible" @update:visible="updateVisible($event)" size="lg" padding="standard">
        <template v-slot:header>
            Assign Appointment Types
        </template>

        <template v-slot:subheader>
            Choose one or more Acuity appointment types used by <strong class="font-bold">{{ groupName }}</strong>.
        </template>

        <div class="flex flex-col gap-4">
            <page-loading v-if="loadingAppointmentTypes"></page-loading>

            <template v-else-if="connectionError">
                <vx-alert color="info" :closeable="false">
                    <template v-slot:icon>
                        <font-awesome-icon widthAuto :icon="faCircleExclamation" />
                    </template>
                    <div class="flex flex-col gap-2">
                        <div>Unable to load appointment types. Please check your Acuity connection.</div>
                        <a :href="`/settings/integrations#/acuity`" class="text-amber-700 hover:text-amber-800 underline">
                            Check Acuity connection
                        </a>
                    </div>
                </vx-alert>
            </template>

            <template v-else-if="availableAppointmentTypes.length === 0">
                <vx-alert color="info" :closeable="false">
                    <template v-slot:icon>
                        <font-awesome-icon widthAuto :icon="faCircleInfo" />
                    </template>
                    No active appointment types found in your Acuity account. Create appointment types in Acuity first, then refresh this page.
                </vx-alert>
            </template>

            <template v-else>
                <div class="flex flex-col gap-3 max-h-96 overflow-y-auto">
                    <div
                        v-for="appointmentType in availableAppointmentTypes"
                        :key="appointmentType.id"
                        data-test="appointment-type-card"
                        class="flex items-center gap-3 p-4 border rounded-xl transition-colors"
                        :class="[
                            isAppointmentTypeMappedToOtherGroup(appointmentType)
                                ? 'border-slate-200 bg-slate-50 opacity-60 cursor-not-allowed'
                                : selectedAppointmentTypeIds.includes(appointmentType.id)
                                    ? 'border-sky-200 bg-sky-50 hover:bg-sky-100/50 cursor-pointer'
                                    : 'border-slate-100 bg-white hover:bg-sky-50/50 cursor-pointer'
                        ]"
                        @click="!isAppointmentTypeMappedToOtherGroup(appointmentType) && toggleAppointmentType(appointmentType.id)"
                        @keydown.space.prevent="!isAppointmentTypeMappedToOtherGroup(appointmentType) && toggleAppointmentType(appointmentType.id)"
                        @keydown.enter.prevent="!isAppointmentTypeMappedToOtherGroup(appointmentType) && toggleAppointmentType(appointmentType.id)"
                    >
                        <vx-checkbox
                            :modelValue="selectedAppointmentTypeIds.includes(appointmentType.id)"
                            :disabled="isAppointmentTypeMappedToOtherGroup(appointmentType)"
                            :aria-label="appointmentType.name"
                        ></vx-checkbox>
                        <div class="flex-1 flex flex-col gap-1">
                            <div class="flex items-center gap-2">
                                <span class="text-sm font-semibold" aria-hidden="true" data-test="appointment-type-name">{{ appointmentType.name }}</span>
                                <span v-if="isAppointmentTypeMappedToOtherGroup(appointmentType)" class="text-xs text-slate-500 italic">
                                    (Mapped to another group)
                                </span>
                            </div>
                            <span class="text-xs text-slate-500 flex items-center gap-1">
                                <font-awesome-icon :icon="faCalendarClock" widthAuto />
                                {{ appointmentType.duration }} min
                            </span>
                        </div>
                    </div>
                </div>
            </template>
        </div>

        <template v-slot:footer>
            <div class="flex flex-col-reverse gap-4 lg:flex-row">
                <vx-button
                    @click="updateVisible(false)"
                    type="button"
                    class="grow lg:w-20 lg:grow-0"
                    size="lg"
                    color="muted"
                >
                    Cancel
                </vx-button>
                <vx-button
                    @click.prevent="submit()"
                    :loading="submitting"
                    type="button"
                    class="grow"
                    size="lg"
                    color="primary"
                >
                    Save
                    <font-awesome-icon widthAuto :icon="faCircleCheck" />
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, type PropType } from 'vue';
import { VxAlert, VxButton, VxCheckbox, VxModal } from '@voxie/frontend-components';
import { faCircleCheck, faCircleInfo, faCircleExclamation, faCalendarClock } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import PageLoading from '~/components/general/PageLoading.vue';
import { useToasted } from '~/composables/useToasted';
import { useGroupDetails } from '../useGroupDetails';
import { acuityClient } from '~/services';
import type { AcuityAppointmentType } from '~/services/acuity/acuityClient.types';
import { catchRequestError } from '~/utils/request';

const toasted = useToasted();
const { group } = useGroupDetails();
const groupName = ref(group.value?.name || '');

const teamId = Spark.state.currentTeam.id;

const visible = defineModel('visible', {
    type: Boolean,
    default: false,
});

const props = defineProps({
    groupId: {
        type: String,
        required: true,
    },
    currentAppointmentTypes: {
        type: Array as PropType<Omit<AcuityAppointmentType, 'group_id'>[]>,
        default: () => [],
    },
});

const emit = defineEmits(['saved']);

const availableAppointmentTypes = ref<AcuityAppointmentType[]>([]);
const selectedAppointmentTypeIds = ref<number[]>([]);
const loadingAppointmentTypes = ref<boolean>(true);
const submitting = ref<boolean>(false);
const connectionError = ref<boolean>(false);

const updateVisible = (value: boolean) => {
    visible.value = value;
};

const isAppointmentTypeMappedToOtherGroup = (appointmentType: AcuityAppointmentType): boolean => {
    return appointmentType.group_id !== null && appointmentType.group_id !== undefined && appointmentType.group_id !== props.groupId;
};

const toggleAppointmentType = (id: number) => {
    const index = selectedAppointmentTypeIds.value.indexOf(id);
    if (index > -1) {
        selectedAppointmentTypeIds.value.splice(index, 1);
    } else {
        selectedAppointmentTypeIds.value.push(id);
    }
};

const loadAppointmentTypes = async () => {
    loadingAppointmentTypes.value = true;
    connectionError.value = false;

    try {
        const response = await acuityClient.getAppointmentTypes({ teamId });
        availableAppointmentTypes.value = response.data.appointment_types;
    } catch (error: unknown) {
        connectionError.value = true;
        toasted.error(catchRequestError(error));
    } finally {
        loadingAppointmentTypes.value = false;
    }
};

const submit = async () => {
    submitting.value = true;

    try {
        const appointmentTypes = selectedAppointmentTypeIds.value.map((id: number) => ({
            appointment_type_id: id,
        }));

        await acuityClient.updateGroupMapping(
            { teamId },
            props.groupId,
            { appointment_types: appointmentTypes }
        );

        toasted.success('Acuity appointment types updated successfully');
        emit('saved');
        updateVisible(false);
    } catch (error: unknown) {
        toasted.error(catchRequestError(error));
    } finally {
        submitting.value = false;
    }
};

watch(visible, (newValue: boolean) => {
    if (newValue) {
        selectedAppointmentTypeIds.value = props.currentAppointmentTypes.map((at) => at.id);
        loadAppointmentTypes();
    } else {
        availableAppointmentTypes.value = [];
        connectionError.value = false;
    }
});

onMounted(() => {
    if (group.value) {
        groupName.value = group.value.name;
    }
});
</script>
